import { useEffect } from 'react'
import { motion, useAnimationControls, useReducedMotion } from 'framer-motion'
import { PageLayout } from 'components/page-layout'
import { PageTitle } from 'utils/page-title'
import { GDateRange } from 'components/basic-blocks/g-date-range'
import { Calendar } from 'components/icons'
import { CanvasHeader } from './components/canvas-header'
import { KeyMetrics } from './components/key-metrics'
import { ChartsRow } from './components/charts-row'
import { DimensionGrid } from './components/dimension-grid'
import { useReportLayoutStore } from './store/use-report-layout-store'
import { useViewsSync } from './data/use-views-sync'
import { useTrackPostHogEvent } from 'utils/posthog'
import { PostHogEvent, PostHogPath } from 'utils/posthog-events'
import './analytics-report-page.css'

/**
 * Analytics Report — a user-arrangeable analytics dashboard.
 *
 * Three stacked, independently-arrangeable sections (metric cards, charts, a
 * dimensional drill-down table). "Customize" swaps each slot and the active
 * view; the layout is a serializable config (see use-report-layout-store) that
 * persists per user/view and restores on load. Data is fetched by each section
 * from the config — the page renders as soon as the layout config is ready and
 * every section streams in with its own loading state.
 */
export const AnalyticsReportPage = () => {
	// Load the user's saved views from the backend + bind the account/asset/user so
	// view mutations persist (§2).
	useViewsSync()

	// Monitoring (§Monitoring): pageview on landing.
	const trackEvent = useTrackPostHogEvent()
	useEffect(() => {
		trackEvent(PostHogEvent.PAGEVIEW, PostHogPath.ANALYTICS_REPORTS, null)
	}, [trackEvent])

	// Report-switch transition — a two-phase dissolve, not a snap. Loading a saved
	// view swaps metrics/charts/table in one render; we dissolve OUT (fade + blur)
	// with the current data, apply the view at the near-invisible point, then
	// dissolve the NEW data back IN. Honors prefers-reduced-motion (instant swap).
	const reportSwapNonce = useReportLayoutStore((s) => s.reportSwapNonce)
	const pendingSwapId = useReportLayoutStore((s) => s.pendingSwapId)
	const applyView = useReportLayoutStore((s) => s.applyView)
	const reduceMotion = useReducedMotion()
	const swapControls = useAnimationControls()

	// OUT phase: dissolve out, then apply the pending view at the invisible point.
	useEffect(() => {
		if (!pendingSwapId) return
		if (reduceMotion) {
			applyView(pendingSwapId)
			return
		}
		let cancelled = false
		swapControls
			.start({
				opacity: 0.12,
				filter: 'blur(6px)',
				transition: { duration: 0.16, ease: [0.4, 0, 1, 1] }
			})
			.then(() => {
				if (!cancelled) applyView(pendingSwapId)
			})
		return () => {
			cancelled = true
		}
	}, [pendingSwapId, reduceMotion, applyView, swapControls])

	// IN phase: dissolve the new report back in once its data has landed.
	useEffect(() => {
		if (reportSwapNonce === 0) return // skip the initial mount
		if (reduceMotion) {
			swapControls.set({ opacity: 1, filter: 'blur(0px)' })
			return
		}
		swapControls.start({
			opacity: [0.12, 1],
			filter: ['blur(6px)', 'blur(0px)'],
			transition: { duration: 0.36, ease: [0.16, 1, 0.3, 1] }
		})
	}, [reportSwapNonce, reduceMotion, swapControls])

	// Fixed-shell page: everything scrolls inside the app's <main>, so the document
	// itself should never scroll. Third-party widgets inject body-level nodes that
	// push <html> taller than the viewport (phantom scrollbar); pin the overflow
	// while this page is mounted and restore it on unmount. The `pa-report-route`
	// class scopes the glass-chrome GPU promotion to this page only (see CSS).
	useEffect(() => {
		const html = document.documentElement
		const prevHtml = html.style.overflow
		const prevBody = document.body.style.overflow
		html.style.overflow = 'hidden'
		document.body.style.overflow = 'hidden'
		html.classList.add('pa-report-route')
		return () => {
			html.style.overflow = prevHtml
			document.body.style.overflow = prevBody
			html.classList.remove('pa-report-route')
		}
	}, [])

	return (
		<>
			<PageTitle pageName="Analytics" />
			<PageLayout
				title="Analytics"
				subtitle="Build custom reports and share them with your workspace team."
				datePicker={
					<GDateRange
						align="right"
						calender={Calendar}
						// GDateRange syncs from/to to the URL; the section data hooks read
						// those params, so no explicit apply handler is needed here.
						// eslint-disable-next-line @typescript-eslint/no-empty-function
						onApply={() => {}}
					/>
				}
			>
				{/* pb-14: breathing room under the table's pagination footer at full
				    scroll. Was pb-28 for a floating Share bar that no longer exists —
				    Esteban (2026-09-03) read the leftover as dead space, "make it half". */}
				<div className="-mt-2 flex flex-col gap-6 pb-14">
					<CanvasHeader />

					{/* Dashboard body — faded as one block on a report switch
					    (swapControls, driven by reportSwapNonce). The header stays
					    outside so its popovers don't flicker during the dissolve. */}
					<motion.div animate={swapControls} className="flex flex-col gap-6">
						<KeyMetrics />
						<ChartsRow />
						{/* data-card-id="table" is the PDF chart-capture + scroll target. */}
						<div data-card-id="table" style={{ borderRadius: 12 }}>
							<DimensionGrid />
						</div>
					</motion.div>
				</div>
			</PageLayout>
		</>
	)
}
